// The stored collection record and its per-scope localStorage keys.
//
// The record is the same shape the single global blob always had; only WHERE
// it lives changed (collectionScope.ts: one key per account, one for guests).
// Pure storage helpers, no React and no network, so the store, the migration
// and the sign-in reconcile all read and write records the same way.

import { ownerStorage } from './offline/ownerStorage'
import { COLLECTION_MIGRATED_AT_KEY, DURABLE_REMOVAL, UNKNOWN_READ, type DurableRemoval } from './collectionMigration.codes'

/**
 * The collection a choice replaced ("You have two collections", or a Switch
 * back), kept on the account record for 30 days so the user can return to it.
 * ONE slot: only the most recent replacement is offered. The server row it
 * names is never deleted by the choice; this is only the way back to it.
 */
export interface PreviousCollection {
  collectionId: string
  cardCount: number | null
  sourceUrl: string | null
  /** Date.now() when it was replaced. */
  at: number
}

/** An owner-bound names-only mint in flight (see StoredCollection.pendingMint). */
export interface PendingMint {
  ownerId: string
  opKey: string
}

// The API's op_key format (16-64 of [A-Za-z0-9_-]).
const OP_KEY_FORMAT = /^[A-Za-z0-9_-]{16,64}$/

function parsePendingMint(raw: unknown): PendingMint | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Partial<PendingMint>
  if (typeof r.ownerId !== 'string' || !r.ownerId || typeof r.opKey !== 'string' || !OP_KEY_FORMAT.test(r.opKey)) return null
  return { ownerId: r.ownerId, opKey: r.opKey }
}

/**
 * An owner-bound claim of the guest collection `collectionId` (see
 * StoredCollection.pendingClaim).
 */
export interface PendingClaim {
  ownerId: string
  collectionId: string
  opKey: string
}

function parsePendingClaim(raw: unknown): PendingClaim | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Partial<PendingClaim>
  if (typeof r.ownerId !== 'string' || !r.ownerId || typeof r.collectionId !== 'string' || !r.collectionId) return null
  if (typeof r.opKey !== 'string' || !OP_KEY_FORMAT.test(r.opKey)) return null
  return { ownerId: r.ownerId, collectionId: r.collectionId, opKey: r.opKey }
}

/**
 * A URL import or refresh whose outcome this device has not settled (see
 * StoredCollection.pendingUrlActions). `path` is the endpoint override (the
 * refresh endpoint) or null for ingest-url-stream; `collectionId` the row it
 * was sent for, or null for a first import; `source` the telemetry source it
 * ran under (absent for the manual URL import); `startedAt` (Date.now()) when
 * it was first sent, for the abandonment window.
 */
export interface PendingUrlAction {
  ownerId: string | null
  url: string
  path: string | null
  collectionId: string | null
  opKey: string
  startedAt: number
  source?: string
}

function parsePendingUrlAction(raw: unknown): PendingUrlAction | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Partial<PendingUrlAction>
  if (r.ownerId !== null && (typeof r.ownerId !== 'string' || !r.ownerId)) return null
  if (typeof r.url !== 'string' || !r.url) return null
  if (r.path !== null && typeof r.path !== 'string') return null
  if (r.collectionId !== null && typeof r.collectionId !== 'string') return null
  if (typeof r.opKey !== 'string' || !OP_KEY_FORMAT.test(r.opKey)) return null
  if (typeof r.startedAt !== 'number' || !Number.isFinite(r.startedAt)) return null
  return {
    ownerId: r.ownerId, url: r.url, path: r.path, collectionId: r.collectionId, opKey: r.opKey, startedAt: r.startedAt,
    ...(typeof r.source === 'string' && r.source ? { source: r.source } : {}),
  }
}

function parsePendingUrlActions(raw: unknown): PendingUrlAction[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: PendingUrlAction[] = []
  for (const item of raw) {
    const a = parsePendingUrlAction(item)
    if (a && !seen.has(a.opKey)) { seen.add(a.opKey); out.push(a) }
  }
  return out
}

export const PREVIOUS_COLLECTION_TTL_MS = 30 * 24 * 60 * 60 * 1000

/** The slot, while it is still offered (30 days). */
export function livePreviousCollection(c: { previousCollection: PreviousCollection | null }, now = Date.now()): PreviousCollection | null {
  const p = c.previousCollection
  if (!p || now - p.at > PREVIOUS_COLLECTION_TTL_MS) return null
  return p
}

function parsePrevious(raw: unknown): PreviousCollection | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Partial<PreviousCollection>
  if (typeof r.collectionId !== 'string' || !r.collectionId || typeof r.at !== 'number' || !Number.isFinite(r.at)) return null
  return {
    collectionId: r.collectionId,
    cardCount: typeof r.cardCount === 'number' ? r.cardCount : null,
    sourceUrl: typeof r.sourceUrl === 'string' ? r.sourceUrl : null,
    at: r.at,
  }
}

export interface StoredCollection {
  collectionId: string | null
  ownedCards: string[] | null
  cardCount: number | null
  sourceUrl: string | null
  version: string | null
  ownedCardsCollectionId: string | null
  // The ownership snapshot of the per-copy layer (plan 2026-10-16 C4): the
  // names held ONLY through proxies, from the same owned-names walk (same
  // version) as ownedCards, bound to the same collection AND user.
  proxyNames: string[]
  copiesUnavailable: boolean
  ownedCardsUserId: string | null
  proxyNamesKnown: boolean
  /**
   * An ACCOUNT record whose pointer was written locally but whose
   * saved_collections upsert has not been confirmed (offline, a failed or
   * dropped upsert). The sign-in reconcile keeps such a pointer over a
   * different server row and retries the upsert, instead of adopting the row
   * and losing the only pointer to it. Always false for the guest record.
   */
  pointerPending: boolean
  /** See PreviousCollection. Account records only. */
  previousCollection: PreviousCollection | null
  /**
   * GUEST record only: a names-only mint the sign-in reconcile has started
   * for `ownerId` (collectionReconcile). Its op_key is stored BEFORE the
   * request and re-sent on every retry, reload or new writer tab until the
   * minted id is durably recorded here, so an ambiguous response never mints
   * a second row.
   */
  pendingMint: PendingMint | null
  /**
   * GUEST record only: the op_key of the sign-in reconcile's claim of
   * `collectionId` as `ownerId` (API spec 5.5). Stored before the claim is
   * sent and re-sent by every later claim of that collection by that
   * account, so a claim whose response was lost (or whose reconcile stopped
   * at the two-collections question) is answered by the server's replay: a
   * legacy row is copied ONCE, not once per load. It lives as long as the
   * guest record does.
   */
  pendingClaim: PendingClaim | null
  /**
   * The ACTIVE scope's unsettled URL imports and refreshes (API spec 5.1),
   * one entry per action keyed by its op_key, each written BEFORE its request
   * is sent. An explicit retry or resume of an entry re-sends its key, so a
   * write the server already made is replayed instead of run again. A new
   * action APPENDS (never replaces another entry); an entry is removed only
   * by its own definitive outcome (compare-delete by key), by the user
   * dismissing it, or by abandonment after the resume window. Capped (see
   * URL_ACTION_CAP in useCollection): when full, a new action is refused.
   */
  pendingUrlActions: PendingUrlAction[]
  /**
   * ACCOUNT record only, with pointerPending and a null collectionId: the
   * foreign collection a 403 dropped. The account row is cleared ONLY while
   * it still names this id (compare-delete); a different row is a legitimate
   * newer pointer and survives.
   */
  pendingNullOf: string | null
  epoch: number | null
}

export const EMPTY_COLLECTION: StoredCollection = {
  collectionId: null,
  ownedCards: null,
  cardCount: null,
  sourceUrl: null,
  version: null,
  ownedCardsCollectionId: null,
  proxyNames: [],
  copiesUnavailable: false,
  ownedCardsUserId: null,
  proxyNamesKnown: false,
  pointerPending: false,
  previousCollection: null,
  pendingMint: null,
  pendingClaim: null,
  pendingUrlActions: [],
  pendingNullOf: null,
  epoch: null,
}

export function normalizeCollection(raw: unknown): StoredCollection {
  if (typeof raw !== 'object' || raw === null) return EMPTY_COLLECTION
  const r = raw as Partial<StoredCollection>
  const collectionId = r.collectionId ?? null
  const ownedCards = Array.isArray(r.ownedCards) ? r.ownedCards : null
  // LEGACY BLOB MIGRATION. A blob written before these fields existed has
  // names but no binding. Binding them to the current pointer preserves today's
  // behaviour for signed-out users (who have no CollectionSync to heal them and
  // would otherwise see their whole collection go unowned), and version:null
  // makes the next sync refetch the canonical set anyway.
  const ownedCardsCollectionId =
    r.ownedCardsCollectionId !== undefined
      ? r.ownedCardsCollectionId ?? null
      : (ownedCards ? collectionId : null)
  return {
    collectionId,
    ownedCards,
    cardCount: r.cardCount ?? null,
    sourceUrl: r.sourceUrl ?? null,
    version: r.version ?? null,
    ownedCardsCollectionId,
    // Older blobs predate the per-copy snapshot: no proxies, unbound.
    proxyNames: Array.isArray(r.proxyNames) ? r.proxyNames.filter(n => typeof n === 'string') : [],
    copiesUnavailable: r.copiesUnavailable === true,
    ownedCardsUserId: typeof r.ownedCardsUserId === 'string' ? r.ownedCardsUserId : null,
    proxyNamesKnown: r.proxyNamesKnown === true,
    pointerPending: r.pointerPending === true,
    previousCollection: parsePrevious(r.previousCollection),
    pendingMint: parsePendingMint(r.pendingMint),
    pendingClaim: parsePendingClaim(r.pendingClaim),
    pendingUrlActions: parsePendingUrlActions(r.pendingUrlActions),
    pendingNullOf: typeof r.pendingNullOf === 'string' && r.pendingNullOf ? r.pendingNullOf : null,
    // Older blobs have no key at all, which is exactly the same "predates the
    // epoch" case as an explicit null - both read as unstamped.
    epoch: typeof r.epoch === 'number' ? r.epoch : null,
  }
}

/** True when the record holds a collection (a pointer or any names). */
export function hasCollection(c: StoredCollection): boolean {
  return c.collectionId !== null || (c.ownedCards?.length ?? 0) > 0
}

export function readCollectionRecord(key: string | null): StoredCollection {
  if (key === null) return EMPTY_COLLECTION
  try {
    const raw = ownerStorage.getItem(key)
    if (!raw) return EMPTY_COLLECTION
    return normalizeCollection(JSON.parse(raw))
  } catch {
    return EMPTY_COLLECTION
  }
}

/**
 * Write a record. False when storage refused it (quota, private mode,
 * disabled), or there is no key (scope unknown).
 */
export function writeCollectionRecord(key: string | null, data: StoredCollection): boolean {
  if (key === null) return false
  try {
    ownerStorage.setItemUnconfirmed(key, JSON.stringify(data))
    return true
  } catch {
    return false
  }
}

/** Write, then read back: true only when the stored text is what was written. */
export function writeCollectionRecordVerified(key: string, data: StoredCollection): boolean {
  const text = JSON.stringify(data)
  try {
    ownerStorage.setItemUnconfirmed(key, text)
    return ownerStorage.getItem(key) === text
  } catch {
    return false
  }
}

/**
 * Write and wait until it is DURABLE (r2 #1): localStorage at once, an
 * account record in the owner DB only after its commit. A memory read-back
 * can not prove that.
 */
export async function writeCollectionRecordDurable(key: string, data: StoredCollection): Promise<boolean> {
  const text = JSON.stringify(data)
  try {
    const w = ownerStorage.write(key, text)
    if (!w.accepted) return false
    // localStorage: written synchronously, read back (a quota-truncating or
    // lying store). The owner DB: its commit is the proof.
    if (typeof w.durable === 'boolean') return w.durable && ownerStorage.getItem(key) === text
    return await w.durable
  } catch {
    return false
  }
}

export type { DurableRemoval }

/**
 * removeCollectionRecord, DURABLE and conditional (r3 blocker 2): the
 * record is read from durable storage (never the memory mirror) and cleared
 * only while `isTarget` says it is still the collection being removed,
 * compare-and-replace inside one transaction (a write that lands meanwhile
 * makes it re-read). 'replaced': the scope now holds ANOTHER collection,
 * which is left alone.
 */
export async function removeCollectionRecordDurable(
  key: string,
  isTarget: (rec: StoredCollection) => boolean,
  opts: { keepPrevious?: PreviousCollection | null } = {},
): Promise<DurableRemoval> {
  if (!markMigrationSettled()) return DURABLE_REMOVAL.failed
  const next = opts.keepPrevious ? JSON.stringify({ ...EMPTY_COLLECTION, previousCollection: opts.keepPrevious }) : null
  for (let attempt = 0; attempt < 3; attempt++) {
    let raw: string | null | 'unknown'
    try {
      raw = await ownerStorage.readDurable(key)
    } catch {
      raw = UNKNOWN_READ
    }
    if (raw === UNKNOWN_READ) return DURABLE_REMOVAL.failed
    if (raw !== null) {
      let rec: StoredCollection
      try {
        rec = normalizeCollection(JSON.parse(raw))
      } catch {
        rec = EMPTY_COLLECTION
      }
      if (!isTarget(rec)) return DURABLE_REMOVAL.replaced
    } else if (next === null) {
      return DURABLE_REMOVAL.removed
    }
    try {
      if (await ownerStorage.replaceIfEquals(key, raw, next)) return DURABLE_REMOVAL.removed
      // Not removable (a store that refuses removeItem): an empty record
      // in its place is a removal too, under the same compare.
      if (next === null && raw !== null && await ownerStorage.replaceIfEquals(key, raw, JSON.stringify(EMPTY_COLLECTION))) return DURABLE_REMOVAL.removed
    } catch {
      return DURABLE_REMOVAL.failed
    }
  }
  return DURABLE_REMOVAL.failed
}

/**
 * Remove a record and VERIFY it: true only when the key is absent, or holds a
 * record with no pointer and no names.
 *
 * `keepPrevious`: a live previousCollection (the account's 30-day way back)
 * survives the removal of the ACTIVE collection. The user removed that
 * collection, not the other one: the record is rewritten as an empty one that
 * carries only the slot, and that write is verified too.
 *
 * A removal is the user's (or the reconcile's) decision about a scoped record,
 * so the one-time legacy migration must never re-copy into it. The migration
 * writes its copy BEFORE its marker; if the marker never landed (storage
 * refused it, or the tab died in between) and this emptied the copy, the next
 * load would copy the legacy blob straight back. So a VERIFIED marker is a
 * precondition: when it cannot be persisted, nothing is removed and the
 * removal fails.
 */
export function removeCollectionRecord(key: string, opts: { keepPrevious?: PreviousCollection | null } = {}): boolean {
  if (!markMigrationSettled()) return false
  if (opts.keepPrevious) {
    return writeCollectionRecordVerified(key, { ...EMPTY_COLLECTION, previousCollection: opts.keepPrevious })
  }
  try {
    ownerStorage.removeItemUnconfirmed(key)
  } catch {
    // Fall back to overwriting with the empty record; verified below either way.
    writeCollectionRecord(key, EMPTY_COLLECTION)
  }
  try {
    const raw = ownerStorage.getItem(key)
    if (raw === null) return true
    return !hasCollection(normalizeCollection(JSON.parse(raw)))
  } catch {
    return false
  }
}

/**
 * The migration marker, present or written now and read back. False when it
 * is absent and cannot be persisted (see removeCollectionRecord).
 */
function markMigrationSettled(): boolean {
  try {
    if (ownerStorage.getItem(COLLECTION_MIGRATED_AT_KEY) !== null) return true
    const value = new Date().toISOString()
    ownerStorage.setItemUnconfirmed(COLLECTION_MIGRATED_AT_KEY, value)
    return ownerStorage.getItem(COLLECTION_MIGRATED_AT_KEY) === value
  } catch {
    return false
  }
}
